<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { computed } from 'vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import NotesList from '~/terra/molecules/notes-list.vue';

const props = defineProps({
  cell_keys: {
    type: Array,
    required: true,
  },
  feature_uid: {
    type: String,
    required: true,
  },
  project_uid: {
    type: String,
    required: true,
  },
  container_view_uid: {
    type: String,
    required: true,
  },
  feature_type: {
    type: String,
    default: '',
  },
  can_edit: {
    type: Boolean,
    default: false,
  },
  onUpdated: {
    type: Function,
    default: () => {},
  },
  cell_notes_map: {
    type: Object,
    default: () => ({}),
  },
});

const emits = defineEmits(['close']);
const { $services, $t } = useCommonImports();

const is_bulk = computed(() => props.cell_keys.length > 1);

const cell_identifier = computed(() => {
  if (is_bulk.value || !props.cell_keys[0])
    return '';
  const [row, col] = props.cell_keys[0].split(':');
  return `${$t('Module')} (${Number.parseInt(row) + 1}, ${Number.parseInt(col) + 1})`;
});

const notes_url = computed(() =>
  `barcode-scans/container/${props.container_view_uid}/project/${props.project_uid}/metadata`);

const notes_with_attachments = computed(() => {
  // For single cell, use data from cell_notes_map
  if (!is_bulk.value && props.cell_keys[0]) {
    const cell_data = props.cell_notes_map[props.cell_keys[0]] || {};
    const notes = cell_data.notes || [];
    const attachments = cell_data.attachments || [];

    const map = attachments.reduce((acc, att) => {
      if (att?.attached_to?.type === 'moduleNotes')
        (acc[att.attached_to.uid] = acc[att.attached_to.uid] || []).push(att);
      return acc;
    }, {});
    return notes.map(n => ({ ...n, attachments: map[n.uid] || [] }));
  }
  return [];
});

function toApiOps(ops = {}) {
  const out = {};
  if (ops.add?.length)
    out.add = ops.add;
  if (ops.update?.length)
    out.update = ops.update;
  if (ops.remove?.length)
    out.delete = ops.remove;
  return out;
}

async function handleUpdate(payload) {
  try {
    const cell_payload = {
      notes: toApiOps(payload.notes),
      attachments: toApiOps(payload.attachments),
    };
    const body = { [props.feature_uid]: {} };
    props.cell_keys.forEach((key) => {
      body[props.feature_uid][key] = cell_payload;
    });
    await $services.terra.patch({
      url: notes_url.value,
      baseURL: import.meta.env.VITE_APP_API_HOST,
      body,
    });
    await props.onUpdated();
    // Close only after bulk operations
    if (is_bulk.value)
      emits('close');
  }
  catch (err) {
    logger.error(err);
  }
}
</script>

<template>
  <div class="fixed bottom-4 right-4 z-[100] w-[440px] max-h-[70vh] bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col">
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
      <div class="flex flex-col gap-0.5">
        <div class="text-md font-semibold">
          {{ is_bulk ? $t('Add note') : $t('Notes') }}
        </div>
        <div v-if="!is_bulk && cell_identifier" class="text-xs text-gray-500">
          {{ cell_identifier }}
        </div>
      </div>
      <HawkButton type="plain" size="sm" icon @click="emits('close')">
        <IconHawkXClose class="w-5 h-5 text-gray-600" />
      </HawkButton>
    </div>
    <div class="flex-1 overflow-auto scrollbar p-4">
      <div v-if="is_bulk" class="text-xs text-gray-600 mb-3">
        {{ $t('Adding to') }} {{ cell_keys.length }} {{ $t('modules') }}
      </div>
      <div v-else-if="!notes_with_attachments.length" class="text-xs text-gray-500 mb-3">
        {{ $t('No notes yet') }}
      </div>
      <NotesList
        :notes="is_bulk ? [] : notes_with_attachments"
        :can_edit="can_edit"
        :show_editor="true"
        :on_update="handleUpdate"
        :check_owner_for_edit="true"
        :attachment_config="{ meta: { service: 'terra', id: feature_uid, type: 'moduleNotes' }, feature_type }"
      />
    </div>
  </div>
</template>
